/* ──────────────────────────────────────────────────────────────────────────────
   Faz 4 — Kalan İade Edilebilir Miktar raporu  ·  /reports/returnable-quantities
   (spec §10 "Ek rapor")

   Kırılım: plasiyer × alış faturası × ürün. Gösterilen sayı faturadaki miktar DEĞİL:
       kalan = fatura_miktarı − daha_önce_iade_edilen
   Bir plasiyerin ALDIĞINDAN FAZLASINI iade etmeye çalışması buradan görülür:
   `overReturned` satırları kırmızı — sunucu kapısı çalışıyorsa OLUŞMAMASI gereken,
   oluştuysa (elle veri düzeltmesi, migration, iptal edilmiş alış faturası) MUTLAKA
   görünmesi gereken satırlardır.

   ══ NEDEN "GETİR" DÜĞMESİ VAR ══════════════════════════════════════════════════
   Uç, satıcı verilmezse her satıcı için ayrı hesap yapar (N sorgu — denormalize
   sayaç YOK, kalan miktar her çağrıda taban tablolardan yeniden hesaplanır). Ekranı
   açar açmaz tüm satıcıları taramak, kimsenin istemediği bir raporu her sekmede
   çalıştırmaktır. Filtre önce seçilir, sorgu sonra çalışır.
   ────────────────────────────────────────────────────────────────────────────── */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { s, authHeaders, Toast, ExcelIcon, exportToExcel } from './listPageStyles';
import { ListPageShell, StatusBadge } from './ui';
import { unwrapLookupItems } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { REPORTS_API, fmtDate, fmtQty, unwrap, rst } from './returnShared';

export function ReturnableQuantitiesReportPage() {
  usePageMeta('İade Edilebilir Miktar', 'Plasiyer × alış faturası × ürün kırılımında kalan iade hakkı.');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ran, setRan] = useState(false);
  const [toast, setToast] = useState(null);

  const [representatives, setRepresentatives] = useState([]);
  const [vendors, setVendors] = useState([]);

  const [repId, setRepId] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [productText, setProductText] = useState('');
  const [onlyRemaining, setOnlyRemaining] = useState(false);
  const [onlyProblem, setOnlyProblem] = useState(false);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/representatives/Select', { headers }).then((r) => (r.ok ? r.json() : [])),
      fetch('/api/vendors/Select', { headers }).then((r) => (r.ok ? r.json() : [])),
    ]).then(([r, v]) => {
      setRepresentatives(unwrapLookupItems(r));
      setVendors(unwrapLookupItems(v));
    }).catch(() => {});
  }, []);

  const repName = useMemo(
    () => representatives.find((x) => x.id === repId)?.name || '',
    [representatives, repId],
  );

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (repId) qs.set('representativeId', repId);
      if (vendorId) qs.set('vendorId', vendorId);
      const res = await fetch(`${REPORTS_API}/returnable-quantities?${qs.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Rapor alınamadı');
      const d = unwrap(await res.json());
      setRows(d?.items || []);
      setRan(true);
    } catch (e) {
      showToast('error', e.message || 'Rapor alınamadı');
    } finally {
      setLoading(false);
    }
  }, [repId, vendorId]);

  const filtered = useMemo(() => {
    const pq = productText.trim().toLocaleLowerCase('tr');
    return rows.filter((r) => {
      if (onlyRemaining && Number(r.remainingQuantity || 0) <= 0) return false;
      if (onlyProblem && !(r.overReturned || r.selectable === false)) return false;
      if (pq) {
        const hay = `${r.productCode || ''} ${r.productName || ''}`.toLocaleLowerCase('tr');
        if (!hay.includes(pq)) return false;
      }
      return true;
    });
  }, [rows, productText, onlyRemaining, onlyProblem]);

  const totals = useMemo(() => filtered.reduce((acc, r) => ({
    invoiced: acc.invoiced + Number(r.invoicedQuantity || 0),
    returned: acc.returned + Number(r.returnedQuantity || 0),
    remaining: acc.remaining + Number(r.remainingQuantity || 0),
  }), { invoiced: 0, returned: 0, remaining: 0 }), [filtered]);

  const overCount = useMemo(() => rows.filter((r) => r.overReturned).length, [rows]);
  const lockedCount = useMemo(() => rows.filter((r) => r.selectable === false).length, [rows]);

  const handleExport = () => {
    if (filtered.length === 0) { showToast('error', 'Dışa aktarılacak veri yok'); return; }
    const cols = [
      { headerName: 'Plasiyer', valueGetter: () => repName || '(tümü)' },
      { headerName: 'Satıcı', valueGetter: ({ row }) => row.vendorName || '' },
      { headerName: 'Alış Faturası', valueGetter: ({ row }) => row.purchaseInvoiceNo || '(fatura no yok)' },
      { headerName: 'Tarih', valueGetter: ({ row }) => fmtDate(row.purchaseInvoiceDate) },
      { headerName: 'Ürün', valueGetter: ({ row }) => [row.productCode, row.productName].filter(Boolean).join(' - ') },
      { headerName: 'Alınan', valueGetter: ({ row }) => Number(row.invoicedQuantity || 0) },
      { headerName: 'İade edilen', valueGetter: ({ row }) => Number(row.returnedQuantity || 0) },
      { headerName: 'Kalan', valueGetter: ({ row }) => Number(row.remainingQuantity || 0) },
      {
        headerName: 'Uyarı',
        valueGetter: ({ row }) => (row.overReturned ? 'Aldığından fazla iade'
          : row.selectable === false ? (row.notSelectableReason || 'Seçilemez') : ''),
      },
    ];
    exportToExcel(filtered, cols, `iade-edilebilir-miktar_${new Date().toISOString().slice(0, 10)}`);
    showToast('success', `${filtered.length} kayıt dışa aktarıldı`);
  };

  const toolbarNode = (
    <div style={rst.filters}>
      <select style={rst.filterSelect} value={repId} onChange={(e) => setRepId(e.target.value)}>
        <option value="">Plasiyer: Tümü</option>
        {representatives.map((r) => <option key={r.id} value={r.id}>{r.name || r.textId}</option>)}
      </select>
      <select style={rst.filterSelect} value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
        <option value="">Satıcı: Tümü</option>
        {vendors.map((v) => <option key={v.id} value={v.id}>{v.name || v.textId}</option>)}
      </select>
      <input style={rst.filterInput} placeholder="Ürün" value={productText}
             onChange={(e) => setProductText(e.target.value)} />
      <span style={{ ...rst.chip, ...(onlyRemaining ? rst.chipOn : null) }} role="button"
            onClick={() => setOnlyRemaining((v) => !v)}>
        Sadece iade edilebilir
      </span>
      <span style={{ ...rst.chip, ...(onlyProblem ? rst.chipDanger : null) }} role="button"
            title="Aldığından fazla iade edilmiş VEYA fatura no'su olmadığı için referans verilemeyen satırlar"
            onClick={() => setOnlyProblem((v) => !v)}>
        ⚠ Sorunlu satırlar
      </span>
      <button style={s.btnPrimary} onClick={run} disabled={loading}>
        {loading ? 'Hesaplanıyor…' : 'Raporu Getir'}
      </button>
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
        <button type="button" title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}
                style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <ExcelIcon size={16} />
        </button>
      </span>
    </div>
  );

  const metaNode = ran ? (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <StatusBadge tone={overCount ? 'bad' : 'ok'}>{overCount} fazla iade</StatusBadge>
      {lockedCount > 0 && <StatusBadge tone="wait">{lockedCount} fatura no bekliyor</StatusBadge>}
    </span>
  ) : null;

  return (
    <div style={s.container}>
      <ListPageShell
        title="Kalan İade Edilebilir Miktar"
        subtitle="Kalan = alınan − daha önce iade edilen. Bir plasiyerin aldığından fazlasını iade etmeye çalışması buradan görülür."
        meta={metaNode}
        toolbar={toolbarNode}
      >
        <div style={{ ...rst.blk, borderTop: 'none' }}>
          {!ran ? (
            <div style={rst.empty}>
              Filtreyi seçip <b>Raporu Getir</b>'e basın. Kalan miktar denormalize bir sayaçtan değil,
              alış faturaları ve iade dağıtımlarından HER ÇAĞRIDA yeniden hesaplanır — satıcı seçilmezse
              tüm satıcılar tek tek taranır.
            </div>
          ) : loading ? (
            <div style={rst.empty}>Hesaplanıyor…</div>
          ) : filtered.length === 0 ? (
            <div style={rst.empty}>Kayıt yok — bu filtreyle iade edilebilir alış satırı bulunamadı.</div>
          ) : (
            <table style={rst.table}>
              <thead>
                <tr>
                  <th style={rst.th}>Plasiyer</th>
                  <th style={rst.th}>Satıcı</th>
                  <th style={rst.th}>Alış Faturası</th>
                  <th style={rst.th}>Tarih</th>
                  <th style={rst.th}>Ürün</th>
                  <th style={{ ...rst.th, ...rst.num }}>Alınan</th>
                  <th style={{ ...rst.th, ...rst.num }}>İade edilen</th>
                  <th style={{ ...rst.th, ...rst.num }}>Kalan</th>
                  <th style={rst.th}>Durum</th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 500).map((r, i) => {
                  const remaining = Number(r.remainingQuantity || 0);
                  return (
                    <tr key={`${r.purchaseInvoiceId}-${r.productId}-${i}`}>
                      {/* Plasiyer–satıcı bağı Warehouse.VendorId'dir; rapor satırı satıcı bazlıdır. */}
                      <td style={rst.td}>{repName || <span style={rst.muted}>(tümü)</span>}</td>
                      <td style={rst.td}>{r.vendorName || '-'}</td>
                      <td style={{ ...rst.td, ...rst.mono }}>
                        {r.purchaseInvoiceNo || <span style={{ ...rst.muted, fontWeight: 400 }}>(fatura no yok)</span>}
                      </td>
                      <td style={rst.td}>{fmtDate(r.purchaseInvoiceDate)}</td>
                      <td style={rst.td}>{[r.productCode, r.productName].filter(Boolean).join(' - ') || '-'}</td>
                      <td style={{ ...rst.td, ...rst.num }}>{fmtQty(r.invoicedQuantity)}</td>
                      <td style={{ ...rst.td, ...rst.num, ...(r.overReturned ? rst.danger : null) }}>
                        {fmtQty(r.returnedQuantity)}
                      </td>
                      {/* BİRİNCİL SAYI — bold. Plasiyerin bugün en fazla ne kadar iade edebileceği. */}
                      <td style={{ ...rst.td, ...rst.num, fontWeight: 800, ...(remaining <= 0 ? rst.muted : null) }}>
                        {fmtQty(remaining)}
                      </td>
                      <td style={rst.td}>
                        {r.overReturned ? (
                          <StatusBadge tone="bad">Aldığından fazla iade</StatusBadge>
                        ) : r.selectable === false ? (
                          <StatusBadge tone="wait">{r.notSelectableReason || 'Seçilemez'}</StatusBadge>
                        ) : remaining <= 0 ? (
                          <span style={rst.muted}>Tamamı iade edildi</span>
                        ) : (
                          <StatusBadge tone="ok">İade edilebilir</StatusBadge>
                        )}
                      </td>
                    </tr>
                  );
                })}
                <tr>
                  <td style={{ ...rst.td, fontWeight: 800 }} colSpan={5}>
                    Toplam · {filtered.length} satır
                  </td>
                  <td style={{ ...rst.td, ...rst.num, fontWeight: 800 }}>{fmtQty(totals.invoiced)}</td>
                  <td style={{ ...rst.td, ...rst.num, fontWeight: 800 }}>{fmtQty(totals.returned)}</td>
                  <td style={{ ...rst.td, ...rst.num, fontWeight: 800 }}>{fmtQty(totals.remaining)}</td>
                  <td style={rst.td} />
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </ListPageShell>

      <Toast toast={toast} />
    </div>
  );
}

export default ReturnableQuantitiesReportPage;
